import { ArrowRight } from "lucide-react";
import { NavLink } from "react-router-dom";
import Seo from "../components/Seo";
import Faq from "../components/Faq";
import Breadcrumbs from "../components/Breadcrumbs";
import { buildFaqSchema, type Crumb } from "../utils/structuredData";
import { useBookAssessment } from "../hooks/useBookAssessment";
import { faqGroups, allFaqs } from "../utils/faqContent";

const FaqPage: React.FC = () => {
  const bookAssessment = useBookAssessment();

  // One combined FAQPage graph covers every visible question on the page, so the
  // full Q&A set is exposed to search engines and AI crawlers in one place.
  const faqSchema = buildFaqSchema(allFaqs);

  const crumbs: Crumb[] = [
    { name: "Home", path: "/" },
    { name: "FAQ", path: "/faq" },
  ];

  return (
    <div className="relative pt-6">
      <Seo
        title="AI Automation FAQ | Contextral"
        description="Answers about Contextral's AI automation services: security, data ownership, compliance, deployment, integrations, support, and industry use cases."
        jsonLd={faqSchema}
      />

      <section>
        <Breadcrumbs items={crumbs} />
        <div className="text-center mb-6 sm:mb-10">
          <h1 className="font-bold text-text-primary mb-4 text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Frequently Asked Questions
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-text-secondary max-w-4xl mx-auto leading-relaxed">
            How Contextral approaches secure workflow automation, data ownership, deployment,
            integrations, support, and the industries we serve.
          </p>
        </div>
      </section>

      <div>
        {faqGroups.map((group) => (
          <Faq key={group.id} id={group.id} title={group.label} items={group.faqs} />
        ))}
      </div>

      <section>
        <div className="relative overflow-hidden bg-gradient-to-r from-primary via-primaryHover to-accentPurple rounded-2xl p-6 sm:p-8 md:p-12 text-center shadow-xl shadow-primary/25">
          <div className="pointer-events-none absolute inset-0 bg-blueprint opacity-30" aria-hidden="true" />
          <h2 className="relative text-2xl sm:text-3xl md:text-4xl font-bold text-text-inverse mb-3 sm:mb-4">
            Still have a question?
          </h2>
          <p className="relative text-surfaceSoft text-base sm:text-lg md:text-xl max-w-3xl mx-auto mb-7">
            We can walk through your environment, data requirements, and the highest-impact
            automation opportunities for your team.
          </p>
          <div className="relative flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <NavLink
              to="/contact"
              className="group w-full sm:w-auto inline-flex items-center justify-center bg-surface text-primary px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:scale-105 transform transition-all duration-300 shadow-lg"
            >
              Contact Our Team
              <ArrowRight aria-hidden="true" className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </NavLink>
            <button
              type="button"
              onClick={() => bookAssessment()}
              className="group w-full sm:w-auto inline-flex items-center justify-center border border-white/60 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white/10 transition-all duration-300"
            >
              Book a Free Automation Assessment
              <ArrowRight aria-hidden="true" className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FaqPage;
